import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CalendarClock, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { scheduleDateSchema, type ActivitySession } from "@learn-flow/contracts";

import { AppShell } from "../../components/app-shell.js";
import {
  createActivityReturnState,
  resolveActivityReturnContext,
} from "../../lib/activity-return-context.js";
import { ApiError } from "../../lib/api.js";
import { createUuid } from "../../lib/random-uuid.js";
import { addLocalDays } from "../calendar/calendar-date.js";
import { calendarQueryKey, loadCalendar } from "../calendar/calendar-api.js";
import { libraryQueryRoot } from "../library/library-api.js";
import { readStoredLibraryContext } from "../library/library-context.js";
import {
  activityDetailKey,
  cancelActivitySession,
  createActivitySession,
  loadActivityDetail,
  updateActivitySession,
} from "./activity-api.js";
import { DeleteSessionDialog } from "./delete-session-dialog.js";
import { ScheduleDatePicker } from "./schedule-date-picker.js";
import { ScheduleTimePicker } from "./schedule-time-picker.js";
import { ScheduleWeekPicker } from "./schedule-week-picker.js";

export function ActivitySchedulePage(): React.JSX.Element {
  const { activityId = "" } = useParams();
  const [parameters] = useSearchParams();
  const sessionId = parameters.get("sessionId");
  const requestedDateResult = scheduleDateSchema.safeParse(parameters.get("date"));
  const requestedDate = requestedDateResult.success ? requestedDateResult.data : null;
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const errorRef = useRef<HTMLDivElement>(null);
  const idempotencyKey = useRef(createUuid());
  const detail = useQuery({
    queryKey: activityDetailKey(activityId),
    queryFn: () => loadActivityDetail(activityId),
    enabled: Boolean(activityId),
  });
  const editing =
    detail.data?.sessions.find((session) => session.id === sessionId) ??
    (sessionId ? "missing" : null);
  const [initializedFor, setInitializedFor] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [calendarStartDate, setCalendarStartDate] = useState<string | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [deleteSession, setDeleteSession] = useState<ActivitySession | null>(null);
  const deleteTriggerRef = useRef<HTMLButtonElement>(null);
  const deleteInFlightRef = useRef(false);
  const initializationKey = detail.data
    ? `${detail.data.id}:${
        typeof editing === "object" && editing ? editing.id : `new:${requestedDate ?? ""}`
      }`
    : "";
  const scheduleInitialized = Boolean(initializationKey) && initializedFor === initializationKey;
  const calendar = useQuery({
    queryKey: calendarQueryKey(calendarStartDate),
    queryFn: () => loadCalendar(calendarStartDate),
    enabled: scheduleInitialized,
  });
  const activityReturnState = createActivityReturnState(
    resolveActivityReturnContext(location.state, readStoredLibraryContext()).destination,
  );

  useEffect(() => {
    document.title = `Schedule learning · MemoKite`;
  }, []);
  useEffect(() => {
    if (!duplicateWarning) return;
    const timer = window.setTimeout(() => errorRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [duplicateWarning]);
  useEffect(() => {
    if (!detail.data) return;
    const key = initializationKey;
    if (initializedFor === key) return;
    const initialDate =
      typeof editing === "object" && editing ? editing.scheduledDate : (requestedDate ?? "");
    setScheduledDate(initialDate);
    setScheduledTime(typeof editing === "object" && editing ? (editing.scheduledTime ?? "") : "");
    setCalendarStartDate(initialDate || null);
    setInitializedFor(key);
  }, [detail.data, editing, initializationKey, initializedFor, requestedDate]);

  const save = useMutation({
    mutationFn: async (allowDuplicate: boolean) => {
      const common = {
        scheduledDate,
        scheduledTime: scheduledTime || null,
        allowDuplicate,
      };
      return typeof editing === "object" && editing
        ? updateActivitySession(editing.id, {
            ...common,
            version: editing.version,
            idempotencyKey: idempotencyKey.current,
          })
        : createActivitySession(activityId, {
            ...common,
            idempotencyKey: idempotencyKey.current,
          });
    },
    onSuccess: async () => {
      idempotencyKey.current = createUuid();
      await invalidateScheduleViews(queryClient, activityId);
      void navigate(`/activities/${activityId}`, {
        replace: true,
        state: {
          ...activityReturnState,
          schedulingNotice:
            typeof editing === "object" && editing ? "Session updated." : "Session scheduled.",
        },
      });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.problem?.code === "EXACT_SESSION_DUPLICATE") {
        setDuplicateWarning(true);
      } else {
        setDuplicateWarning(false);
      }
    },
  });
  useEffect(() => {
    if (!save.isError || duplicateWarning) return;
    errorRef.current?.focus();
  }, [duplicateWarning, save.isError]);
  const deleteMutation = useMutation({
    mutationFn: (session: ActivitySession) =>
      cancelActivitySession(session.id, {
        version: session.version,
        idempotencyKey: createUuid(),
      }),
    onSuccess: async () => {
      deleteInFlightRef.current = false;
      setDeleteSession(null);
      await invalidateScheduleViews(queryClient, activityId);
      void navigate(`/activities/${activityId}`, {
        replace: true,
        state: {
          ...activityReturnState,
          schedulingNotice: "Session deleted from your schedule. Its history is still kept.",
        },
      });
    },
    onError: () => {
      deleteInFlightRef.current = false;
    },
  });
  const dismissDeleteDialog = () => {
    if (deleteMutation.isPending) return;
    setDeleteSession(null);
    window.setTimeout(() => deleteTriggerRef.current?.focus(), 0);
  };

  if (detail.isPending) return <ScheduleState title="Loading schedule" busy />;
  if (detail.isError || editing === "missing") {
    return (
      <ScheduleState
        title={editing === "missing" ? "Session not found" : "Schedule could not be loaded"}
      />
    );
  }
  const activity = detail.data;
  const immutable = typeof editing === "object" && editing && editing.status !== "scheduled";
  const invalid = !scheduledDate;
  return (
    <AppShell mainClassName="schedule-screen">
      <nav className="activity-detail-topbar" aria-label="Schedule navigation">
        <Link className="back-button" to={`/activities/${activityId}`} state={activityReturnState}>
          <ArrowLeft size={17} /> Back to activity
        </Link>
      </nav>
      <section
        className="schedule-card schedule-card-with-calendar"
        aria-labelledby="schedule-page-heading"
      >
        <h1 id="schedule-page-heading">
          {editing ? "Edit this session" : "Schedule another session"}
        </h1>
        <p className="schedule-lead">
          {activity.title} · {activity.estimatedMinutes} minute activity
        </p>
        {immutable ? (
          <div className="schedule-message" role="status">
            <strong>This session is part of your history.</strong>
            <span>Completed and deleted sessions cannot be edited.</span>
          </div>
        ) : (
          <div className="schedule-planning-layout">
            <form
              className="schedule-form"
              onSubmit={(event) => {
                event.preventDefault();
                setDuplicateWarning(false);
                save.mutate(false);
              }}
            >
              <div className="schedule-toolbar">
                <ScheduleDatePicker
                  value={scheduledDate}
                  onChange={(date) => {
                    setScheduledDate(date);
                    setCalendarStartDate(date);
                  }}
                />
                <ScheduleTimePicker value={scheduledTime} onChange={setScheduledTime} />
                <div className="schedule-actions">
                  <button
                    className="primary-button"
                    type="submit"
                    disabled={invalid || save.isPending}
                  >
                    <CalendarClock size={16} />
                    {save.isPending ? "Saving…" : editing ? "Save changes" : "Schedule session"}
                  </button>
                  <Link
                    className="soft-button"
                    to={`/activities/${activityId}`}
                    state={activityReturnState}
                  >
                    Cancel
                  </Link>
                  {typeof editing === "object" && editing ? (
                    <button
                      ref={deleteTriggerRef}
                      type="button"
                      className="danger-button"
                      onClick={() => {
                        deleteMutation.reset();
                        setDeleteSession(editing);
                      }}
                    >
                      <Trash2 size={15} /> Delete session
                    </button>
                  ) : null}
                </div>
              </div>
              {duplicateWarning ? (
                <div ref={errorRef} className="schedule-message warning" role="alert" tabIndex={-1}>
                  <strong>An identical session is already scheduled.</strong>
                  <span>You can adjust the details or intentionally schedule another copy.</span>
                  <button
                    type="button"
                    className="soft-button"
                    disabled={save.isPending}
                    onClick={() => {
                      save.mutate(true);
                    }}
                  >
                    Schedule duplicate anyway
                  </button>
                </div>
              ) : null}
              {save.isError && !duplicateWarning ? (
                <div ref={errorRef} className="schedule-message error" role="alert" tabIndex={-1}>
                  <strong>The session was not saved.</strong>
                  <span>{save.error instanceof ApiError ? save.error.message : "Try again."}</span>
                </div>
              ) : null}
            </form>
            <ScheduleWeekPicker
              calendar={calendar.data}
              isError={calendar.isError}
              isFetching={calendar.isFetching}
              isPending={calendar.isPending}
              selectedDate={scheduledDate}
              onPrevious={() => {
                if (calendar.data) {
                  setCalendarStartDate(addLocalDays(calendar.data.range.startDate, -7));
                }
              }}
              onNext={() => {
                if (calendar.data) {
                  setCalendarStartDate(addLocalDays(calendar.data.range.startDate, 7));
                }
              }}
              onRetry={() => void calendar.refetch()}
              onSelectDate={(date) => {
                setScheduledDate(date);
              }}
            />
          </div>
        )}
      </section>
      {deleteSession ? (
        <DeleteSessionDialog
          session={deleteSession}
          pending={deleteMutation.isPending}
          failed={deleteMutation.isError}
          onDismiss={dismissDeleteDialog}
          onConfirm={() => {
            if (deleteInFlightRef.current) return;
            deleteInFlightRef.current = true;
            deleteMutation.mutate(deleteSession);
          }}
        />
      ) : null}
    </AppShell>
  );
}

function ScheduleState({ title, busy = false }: { title: string; busy?: boolean }) {
  return (
    <AppShell mainClassName="schedule-screen">
      <section className="schedule-card" aria-busy={busy} aria-labelledby="schedule-state-heading">
        <CalendarClock size={30} />
        <h1 id="schedule-state-heading">{title}</h1>
        {!busy ? (
          <Link className="soft-button" to="/library">
            Return to Library
          </Link>
        ) : null}
      </section>
    </AppShell>
  );
}

async function invalidateScheduleViews(
  queryClient: ReturnType<typeof useQueryClient>,
  activityId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: activityDetailKey(activityId) }),
    queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
    queryClient.invalidateQueries({ queryKey: ["today"] }),
    queryClient.invalidateQueries({ queryKey: ["calendar"] }),
  ]);
}
